/* global Office */

export interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresAt: number;
  tokenType: string;
}

export interface UserInfo {
  sub: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  roles?: string[];
}

export interface AuthMessage {
  type: "auth_success" | "auth_error";
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
  expiresIn?: number;
  tokenType?: string;
  error?: string;
}

// Keycloak Configuration — values read at runtime from window.__CONFIG__
// (injected by entrypoint.sh in Docker, or from config.js in local dev)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _cfg = (window as any).__CONFIG__ || {};
export const KEYCLOAK_CONFIG = {
  authority: _cfg.KEYCLOAK_AUTHORITY as string,
  clientId: _cfg.KEYCLOAK_CLIENT_ID as string,
  redirectUri: (_cfg.ADDIN_BASE_URL as string) + "/dialog/auth-callback.html",
  authStartUrl: (_cfg.ADDIN_BASE_URL as string) + "/dialog/auth-start.html",
  scope: "openid profile email",
};


export type AuthStateChangeCallback = (isAuthenticated: boolean, user: UserInfo | null) => void;

const STORAGE_KEY = "word_addin_auth";

class TokenManager {
  private tokenInfo: TokenInfo | null = null;
  private userInfo: UserInfo | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private authStateCallbacks: AuthStateChangeCallback[] = [];

  constructor() {
    // Restore tokens from sessionStorage on initialization
    this.restoreFromStorage();
  }

  /**
   * Save tokens to sessionStorage
   */
  private saveToStorage(): void {
    if (this.tokenInfo && this.userInfo) {
      try {
        const data = {
          tokenInfo: this.tokenInfo,
          userInfo: this.userInfo,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.warn("Failed to save auth state to storage:", error);
      }
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Restore tokens from sessionStorage
   */
  private restoreFromStorage(): void {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.tokenInfo && data.tokenInfo.expiresAt > Date.now()) {
          this.tokenInfo = data.tokenInfo;
          this.userInfo = data.userInfo;
          // Schedule token refresh
          this.scheduleTokenRefresh();
        } else {
          // Token expired, clear storage
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      console.warn("Failed to restore auth state from storage:", error);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Subscribe to authentication state changes
   */
  onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    this.authStateCallbacks.push(callback);
    // Return unsubscribe function
    return () => {
      const index = this.authStateCallbacks.indexOf(callback);
      if (index > -1) {
        this.authStateCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Notify all subscribers of auth state change
   */
  private notifyAuthStateChange(): void {
    const isAuthenticated = this.isAuthenticated();
    const user = this.userInfo;
    this.authStateCallbacks.forEach((callback) => {
      try {
        callback(isAuthenticated, user);
      } catch (error) {
        console.error("Auth state callback error:", error);
      }
    });
  }

  /**
   * Open login dialog using Office Dialog API
   */
  async login(): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.context.ui.displayDialogAsync(
        KEYCLOAK_CONFIG.authStartUrl,
        {
          height: 60,
          width: 30,
          promptBeforeOpen: false,
        },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            console.error("Dialog failed:", asyncResult.error.message);
            reject(new Error(asyncResult.error.message));
            return;
          }

          const dialog = asyncResult.value;

          // Handle messages from dialog
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg: Office.DialogParentMessageReceivedEventArgs) => {
              dialog.close();

              try {
                const message: AuthMessage = JSON.parse(arg.message || "{}");

                if (message.type === "auth_success") {
                  // Store tokens
                  this.tokenInfo = {
                    accessToken: message.accessToken!,
                    refreshToken: message.refreshToken!,
                    idToken: message.idToken!,
                    expiresAt: Date.now() + (message.expiresIn || 300) * 1000,
                    tokenType: message.tokenType || "Bearer",
                  };

                  // Decode user info from ID token
                  this.userInfo = this.decodeIdToken(message.idToken!);

                  // Save to storage
                  this.saveToStorage();

                  // Setup auto-refresh
                  this.scheduleTokenRefresh();

                  // Notify subscribers
                  this.notifyAuthStateChange();

                  resolve();
                } else if (message.type === "auth_error") {
                  reject(new Error(message.error || "Authentication failed"));
                } else {
                  reject(new Error("Unknown message type"));
                }
              } catch (error) {
                reject(new Error("Failed to parse auth response"));
              }
            }
          );

          // Handle dialog close events
          dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: { error: number }) => {
            if (arg.error === 12006) {
              // Dialog closed by user
              reject(new Error("Authentication cancelled by user"));
            } else {
              reject(new Error(`Dialog event error: ${arg.error}`));
            }
          });
        }
      );
    });
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Optionally call Keycloak logout endpoint
    if (this.tokenInfo?.idToken) {
      try {
        const logoutUrl = new URL(`${KEYCLOAK_CONFIG.authority}/protocol/openid-connect/logout`);
        logoutUrl.searchParams.set("id_token_hint", this.tokenInfo.idToken);
        logoutUrl.searchParams.set("post_logout_redirect_uri", window.location.origin);

        // Fire and forget - we don't need to wait for Keycloak response
        fetch(logoutUrl.toString(), { mode: "no-cors" }).catch(() => {
          // Ignore errors
        });
      } catch (error) {
        console.warn("Logout request failed:", error);
      }
    }

    // Clear tokens
    this.tokenInfo = null;
    this.userInfo = null;

    // Clear storage
    this.saveToStorage();

    // Notify subscribers
    this.notifyAuthStateChange();
  }

  /**
   * Get current access token, refreshing if necessary
   */
  async getAccessToken(): Promise<string> {
    if (!this.tokenInfo) {
      throw new Error("Not authenticated");
    }

    // Check if token is expired or about to expire (within 30 seconds)
    const bufferTime = 30 * 1000;
    if (Date.now() >= this.tokenInfo.expiresAt - bufferTime) {
      await this.refreshTokens();
    }

    return this.tokenInfo.accessToken;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.tokenInfo !== null && Date.now() < this.tokenInfo.expiresAt;
  }

  /**
   * Get current user info
   */
  getUserInfo(): UserInfo | null {
    return this.userInfo;
  }

  /**
   * Refresh tokens using refresh token
   */
  private async refreshTokens(): Promise<void> {
    if (!this.tokenInfo?.refreshToken) {
      throw new Error("No refresh token available");
    }

    const tokenUrl = `${KEYCLOAK_CONFIG.authority}/protocol/openid-connect/token`;

    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: KEYCLOAK_CONFIG.clientId,
      refresh_token: this.tokenInfo.refreshToken,
    });

    try {
      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();

      this.tokenInfo = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        idToken: data.id_token,
        expiresAt: Date.now() + data.expires_in * 1000,
        tokenType: data.token_type,
      };

      // Update user info
      this.userInfo = this.decodeIdToken(data.id_token);

      // Save to storage
      this.saveToStorage();

      // Reschedule refresh
      this.scheduleTokenRefresh();

      // Notify subscribers
      this.notifyAuthStateChange();
    } catch (error) {
      console.error("Token refresh error:", error);
      // Clear tokens on refresh failure
      this.tokenInfo = null;
      this.userInfo = null;
      this.saveToStorage();
      this.notifyAuthStateChange();
      throw error;
    }
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.tokenInfo) return;

    // Refresh 60 seconds before expiration
    const refreshTime = this.tokenInfo.expiresAt - Date.now() - 60 * 1000;

    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(async () => {
        try {
          await this.refreshTokens();
        } catch (error) {
          console.error("Auto-refresh failed:", error);
        }
      }, refreshTime);
    }
  }

  /**
   * Decode JWT ID token to get user info
   */
  private decodeIdToken(idToken: string): UserInfo {
    try {
      const parts = idToken.split(".");
      if (parts.length !== 3) {
        throw new Error("Invalid token format");
      }

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      const parsed = JSON.parse(decoded);

      return {
        sub: parsed.sub,
        name: parsed.name,
        preferred_username: parsed.preferred_username,
        email: parsed.email,
        email_verified: parsed.email_verified,
        given_name: parsed.given_name,
        family_name: parsed.family_name,
        roles: parsed.realm_access?.roles || [],
      };
    } catch (error) {
      console.error("Failed to decode ID token:", error);
      return { sub: "unknown" };
    }
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();
